import React, { useState } from 'react';
import { toast } from 'sonner';
import { CreditCard, ExternalLink, Smartphone, Copy, Check } from 'lucide-react';

/**
 * DirectBuyEthPanel - Zero API setup required
 * Clients use their own Transak/MoonPay account to buy ETH
 * ETH goes directly to their BETH wallet
 */
export const DirectBuyEthPanel = ({ usd, walletAddress, walletLabel }) => {
  const [copied, setCopied] = useState(false);
  const amount = Number(usd) || 50;

  // Build Transak public URL - no API key needed
  const transakUrl = `https://global.transak.com/?` + new URLSearchParams({
    cryptoCurrencyCode: 'ETH',
    network: 'ethereum',
    defaultCryptoCurrency: 'ETH',
    fiatCurrency: 'USD',
    fiatAmount: String(amount),
    walletAddress: walletAddress || '',
    themeColor: '10B981', // Emerald
  }).toString();

  // MoonPay public URL
  const moonpayUrl = `https://buy.moonpay.com/?` + new URLSearchParams({
    currencyCode: 'eth',
    baseCurrencyCode: 'usd',
    baseCurrencyAmount: String(amount),
    walletAddress: walletAddress || '',
  }).toString();

  // Coinbase direct link
  const coinbaseUrl = 'https://www.coinbase.com/price/ethereum';

  // Copy wallet address
  const copyAddress = () => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      toast.success('Wallet address copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!walletAddress) {
    return (
      <div className="rounded-lg border border-slate-600 bg-slate-800/50 p-4" data-testid="direct-buy-no-wallet">
        <p className="text-sm text-slate-400">Connect a wallet first to see ETH purchase options.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-sky-500/50 bg-sky-500/5 p-4" data-testid="direct-buy-eth-panel">
      <p className="text-sm text-sky-200 font-semibold flex items-center gap-2">
        <CreditCard className="w-4 h-4" /> Buy ETH Directly (Easiest)
      </p>
      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
        <Smartphone className="w-3 h-3" /> Use your own account on any exchange. Card, Apple Pay, bank transfer accepted.
      </p>

      {/* Wallet address display */}
      <div className="mt-3 bg-slate-900/60 rounded-lg p-3">
        <p className="text-xs text-slate-500 mb-1">Send ETH to your {walletLabel || 'BETH'} wallet:</p>
        <div className="flex items-center gap-2">
          <code className="text-sm text-sky-300 font-mono break-all flex-1" data-testid="direct-buy-wallet-address">
            {walletAddress}
          </code>
          <button 
            onClick={copyAddress}
            className="p-1.5 text-slate-400 hover:text-white rounded transition-colors"
            data-testid="direct-buy-copy-btn"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Quick buy buttons */}
      <div className="mt-4 space-y-2">
        <p className="text-xs text-slate-500 font-medium">Quick Buy Options (${amount} USD):</p>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {/* Transak */}
          <a 
            href={transakUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="direct-buy-transak-btn"
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" /> Transak
          </a>

          {/* MoonPay */}
          <a 
            href={moonpayUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="direct-buy-moonpay-btn"
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" /> MoonPay
          </a>

          {/* Coinbase */}
          <a 
            href={coinbaseUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="direct-buy-coinbase-btn"
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" /> Coinbase
          </a>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-4 text-xs text-slate-400 space-y-1">
        <p className="font-medium text-slate-300">How it works:</p>
        <ol className="list-decimal list-inside space-y-0.5 text-slate-500">
          <li>Click any button above to open the exchange</li>
          <li>Create an account or sign in</li>
          <li>Buy ETH with your card, Apple Pay, or bank</li>
          <li>Your wallet address is pre-filled - just confirm</li>
          <li>ETH arrives in 5-15 minutes</li>
        </ol>
      </div>

      <p className="mt-3 text-xs text-amber-300/80">
        Tip: Transak & MoonPay have the wallet pre-filled. For Coinbase, copy the address above and paste it when sending.
      </p>
    </div>
  );
};
